import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { useQueryBuilderCache } from '../../context/QueryBuilderCacheContext';

export interface AvailableGroup {
    id: number;
    userId: number;
    description: string;
    source: string;
    updateDate: string;
}

const GROUPS_CACHE_KEY = 'available_groups';

export const useAvailableGroups = () => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const cache = useQueryBuilderCache();

    const [groups, setGroups] = useState<AvailableGroup[]>(() =>
        cache?.get<AvailableGroup[]>(GROUPS_CACHE_KEY)?.data ?? []
    );
    const [loading, setLoading] = useState<boolean>(() =>
        !cache?.get<AvailableGroup[]>(GROUPS_CACHE_KEY)
    );
    const [error, setError] = useState<string | null>(null);

    const fetchGroups = useCallback(async (forceRefresh = false) => {
        if (!isReady) return;
        if (!forceRefresh) {
            const hit = cache?.get<AvailableGroup[]>(GROUPS_CACHE_KEY);
            if (hit) { setGroups(hit.data); setLoading(false); return; }
        }
        setLoading(true);
        setError(null);
        try {
            const response = await authFetch('/getAvailableGroupsQuery', { method: 'GET' }) as AvailableGroup[] | { member?: AvailableGroup[] } | { 'hydra:member'?: AvailableGroup[] };
            const groupsData: AvailableGroup[] = Array.isArray(response)
                ? response
                : (response as { member?: AvailableGroup[] }).member
                    ?? (response as { 'hydra:member'?: AvailableGroup[] })['hydra:member']
                    ?? [];
            setGroups(groupsData);
            cache?.set(GROUPS_CACHE_KEY, groupsData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur de chargement des groupes');
        } finally {
            setLoading(false);
        }
    }, [authFetch, isReady, cache]);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    return useMemo(() => ({
        groups,
        loading,
        error,
        refetch: () => fetchGroups(true)
    }), [groups, loading, error, fetchGroups]);
};
